import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeys } from '@splitfiesta/core';
import {
  CreateSettlementDto,
  PaginatedResponseDto,
  SettlementResponseDto,
  SettlementStatus,
  SuccessResponseDto,
} from '@splitfiesta/shared';

import { useTypedApi } from '@/hooks/useTypedApi';

import {
  trackPaymentRecorded,
  trackSettlementConfirmed,
  trackSettlementCreated,
  trackSettlementRejected,
} from '@/lib/analytics';

// Fetch group settlements with pagination
export const useGroupSettlementsQuery = (
  groupId: string,
  params?: {
    page?: number;
    limit?: number;
    status?: SettlementStatus;
  },
  enabled = true
) => {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.settlements.group(groupId, params),
    queryFn: async () => {
      const response = await api.settlements.list(groupId, {
        page: params?.page,
        limit: params?.limit,
        status: params?.status,
      });
      return response.data;
    },
    enabled: enabled && !!groupId,
  });
};

// Fetch group settlements with infinite scroll
export const useGroupSettlementsInfiniteQuery = (
  groupId: string,
  params?: {
    limit?: number;
    status?: SettlementStatus;
  },
  enabled = true
) => {
  const api = useTypedApi();

  return useInfiniteQuery({
    queryKey: queryKeys.settlements.group(groupId, params),
    queryFn: async ({
      pageParam = 1,
    }): Promise<
      | { settlements: SettlementResponseDto[] }
      | PaginatedResponseDto<SettlementResponseDto>
    > => {
      const response = await api.settlements.list(groupId, {
        page: pageParam,
        limit: params?.limit,
        status: params?.status,
      });
      // Handle both SuccessResponseDto and direct response
      if ('data' in response) {
        return response.data as { settlements: SettlementResponseDto[] };
      }
      return response as { settlements: SettlementResponseDto[] };
    },
    getNextPageParam: (
      lastPage:
        | { settlements: SettlementResponseDto[] }
        | PaginatedResponseDto<SettlementResponseDto>,
      pages
    ) => {
      const currentPage = pages.length;
      // Handle both paginated response and direct settlements array
      if (lastPage && 'total' in lastPage && 'limit' in lastPage) {
        const totalPages = Math.ceil(lastPage.total / (lastPage.limit || 25));
        return currentPage < totalPages ? currentPage + 1 : undefined;
      }
      // If it's just a settlements array, check if there are more results
      const settlements = lastPage?.settlements || [];
      const hasMore = settlements.length === (params?.limit || 25);
      return hasMore ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: enabled && !!groupId,
  });
};

// Fetch user settlements
export const useUserSettlementsQuery = (
  params?: {
    page?: number;
    limit?: number;
    status?: SettlementStatus;
  },
  enabled = true
) => {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.settlements.user(params),
    queryFn: async () => {
      const queryString = params
        ? `?${new URLSearchParams(
            Object.entries(params)
              .filter(([_, v]) => v !== undefined)
              .map(([k, v]) => [k, String(v)])
          ).toString()}`
        : '';
      const response = await api.get<
        PaginatedResponseDto<SettlementResponseDto>
      >(`/settlements/user${queryString}`);
      // Handle both SuccessResponseDto and direct response
      if (response && typeof response === 'object' && 'data' in response) {
        return response.data;
      }
      return response;
    },
    enabled,
  });
};

// Get settlement summary for a group
export const useSettlementSummaryQuery = (groupId: string, enabled = true) => {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.settlements.summary(groupId),
    queryFn: async () => {
      const response = await api.settlements.summary(groupId);
      // Handle both SuccessResponseDto and direct response
      if (response && typeof response === 'object' && 'data' in response) {
        return (response as SuccessResponseDto<unknown>).data;
      }
      return response;
    },
    enabled: enabled && !!groupId,
  });
};

// Create settlement mutation
export const useCreateSettlementMutation = () => {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSettlementDto) => {
      const response = await api.post<SettlementResponseDto>(
        '/settlements',
        data
      );
      // Handle both SuccessResponseDto and direct response
      if ('data' in response) {
        return response.data;
      }
      return response;
    },
    onSuccess: (_, variables) => {
      // Track analytics
      trackSettlementCreated(
        variables.groupId,
        variables.amount,
        variables.currency
      );

      // Invalidate ALL group settlements queries (including filtered ones)
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'group', variables.groupId],
      });
      // Invalidate user settlements
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'user'],
      });
      // Invalidate group balances
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupBalances(variables.groupId),
      });
      // Refetch simplified debts for both filtered and unfiltered queries (force immediate refetch)
      queryClient.refetchQueries({
        queryKey: queryKeys.simplifiedDebts(variables.groupId, true),
      });
      queryClient.refetchQueries({
        queryKey: queryKeys.simplifiedDebts(variables.groupId, false),
      });
      // Refetch group with dashboard (force immediate refetch)
      queryClient.refetchQueries({
        queryKey: queryKeys.groupWithDashboard(variables.groupId),
      });
      // Invalidate settlement summary
      queryClient.invalidateQueries({
        queryKey: queryKeys.settlements.summary(variables.groupId),
      });
    },
  });
};

/**
 * Record payment mutation (creditor-initiated or owner-initiated settlement)
 * Used when a creditor wants to record that they received payment from a debtor,
 * or when a group owner records a payment between two members.
 * The settlement is auto-completed (no confirmation required from debtor).
 */
export const useRecordPaymentMutation = () => {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      groupId: string;
      fromUserId: string; // The debtor (person who paid)
      amount: number;
      currency: string;
      method?: string;
      notes?: string;
      // Owner-initiated fields
      initiatedByOwner?: boolean;
      toUserId?: string; // The creditor (required when initiatedByOwner is true)
    }) => {
      // Create settlement with appropriate flag
      // For creditor-initiated: caller is creditor (toUserId), fromUserId = debtor who paid
      // For owner-initiated: caller is owner, fromUserId = debtor, toUserId = creditor
      const payload: CreateSettlementDto = {
        groupId: data.groupId,
        fromUserId: data.fromUserId, // The debtor who paid
        amount: data.amount,
        currency: data.currency,
        method: data.method,
        notes: data.notes,
        // Use owner-initiated if specified, otherwise creditor-initiated
        ...(data.initiatedByOwner && data.toUserId
          ? { initiatedByOwner: true, toUserId: data.toUserId }
          : { initiatedByCreditor: true }),
      };
      const response = await api.post<SettlementResponseDto>(
        '/settlements',
        payload
      );
      // Handle both SuccessResponseDto and direct response
      if ('data' in response) {
        return response.data;
      }
      return response;
    },
    onSuccess: async (_, variables) => {
      // Track analytics
      trackPaymentRecorded(
        variables.groupId,
        variables.amount,
        variables.currency
      );

      // Invalidate ALL group settlements queries (including filtered ones)
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'group', variables.groupId],
      });
      // Invalidate user settlements
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'user'],
      });
      // Invalidate group balances
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupBalances(variables.groupId),
      });
      // Await refetch of simplified debts to ensure fresh data before UI updates
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: queryKeys.simplifiedDebts(variables.groupId, true),
        }),
        queryClient.refetchQueries({
          queryKey: queryKeys.simplifiedDebts(variables.groupId, false),
        }),
        queryClient.refetchQueries({
          queryKey: queryKeys.groupWithDashboard(variables.groupId),
        }),
      ]);
      // Invalidate settlement summary
      queryClient.invalidateQueries({
        queryKey: queryKeys.settlements.summary(variables.groupId),
      });
    },
  });
};

// Confirm settlement mutation
export const useConfirmSettlementMutation = () => {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      settlementId,
    }: {
      settlementId: string;
      groupId: string;
    }) => {
      const response = await api.post<SettlementResponseDto>(
        `/settlements/${settlementId}/confirm`
      );
      // Handle both SuccessResponseDto and direct response
      if (response && typeof response === 'object' && 'data' in response) {
        return (response as SuccessResponseDto<unknown>).data;
      }
      return response;
    },
    onSuccess: (_, variables) => {
      // Track analytics
      trackSettlementConfirmed(variables.groupId, variables.settlementId);

      // Invalidate ALL group settlements queries (including filtered ones)
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'group', variables.groupId],
      });
      // Invalidate user settlements
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'user'],
      });
      // Invalidate group balances
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupBalances(variables.groupId),
      });
      // Refetch simplified debts for both filtered and unfiltered queries (force immediate refetch)
      queryClient.refetchQueries({
        queryKey: queryKeys.simplifiedDebts(variables.groupId, true),
      });
      queryClient.refetchQueries({
        queryKey: queryKeys.simplifiedDebts(variables.groupId, false),
      });
      // Refetch group with dashboard (force immediate refetch)
      queryClient.refetchQueries({
        queryKey: queryKeys.groupWithDashboard(variables.groupId),
      });
      // Invalidate settlement summary
      queryClient.invalidateQueries({
        queryKey: queryKeys.settlements.summary(variables.groupId),
      });
    },
  });
};

// Cancel settlement mutation (for sender)
export const useCancelSettlementMutation = () => {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      settlementId,
    }: {
      settlementId: string;
      groupId: string;
    }) => {
      const response = await api.post<SettlementResponseDto>(
        `/settlements/${settlementId}/cancel`
      );
      // Handle both SuccessResponseDto and direct response
      if (response && typeof response === 'object' && 'data' in response) {
        return (response as SuccessResponseDto<unknown>).data;
      }
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate ALL group settlements queries (including filtered ones)
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'group', variables.groupId],
      });
      // Invalidate user settlements
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'user'],
      });
      // Invalidate group balances
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupBalances(variables.groupId),
      });
      // Refetch simplified debts for both filtered and unfiltered queries (force immediate refetch)
      queryClient.refetchQueries({
        queryKey: queryKeys.simplifiedDebts(variables.groupId, true),
      });
      queryClient.refetchQueries({
        queryKey: queryKeys.simplifiedDebts(variables.groupId, false),
      });
      // Refetch group with dashboard (force immediate refetch)
      queryClient.refetchQueries({
        queryKey: queryKeys.groupWithDashboard(variables.groupId),
      });
      // Invalidate settlement summary
      queryClient.invalidateQueries({
        queryKey: queryKeys.settlements.summary(variables.groupId),
      });
    },
  });
};

// Reject settlement mutation (for recipient)
export const useRejectSettlementMutation = () => {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      settlementId,
    }: {
      settlementId: string;
      groupId: string;
    }) => {
      const response = await api.post<SettlementResponseDto>(
        `/settlements/${settlementId}/reject`
      );
      // Handle both SuccessResponseDto and direct response
      if (response && typeof response === 'object' && 'data' in response) {
        return (response as SuccessResponseDto<unknown>).data;
      }
      return response;
    },
    onSuccess: (_, variables) => {
      // Track analytics
      trackSettlementRejected(variables.groupId, variables.settlementId);

      // Invalidate ALL group settlements queries (including filtered ones)
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'group', variables.groupId],
      });
      // Invalidate user settlements
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'user'],
      });
      // Invalidate group balances
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupBalances(variables.groupId),
      });
      // Refetch simplified debts for both filtered and unfiltered queries (force immediate refetch)
      queryClient.refetchQueries({
        queryKey: queryKeys.simplifiedDebts(variables.groupId, true),
      });
      queryClient.refetchQueries({
        queryKey: queryKeys.simplifiedDebts(variables.groupId, false),
      });
      // Refetch group with dashboard (force immediate refetch)
      queryClient.refetchQueries({
        queryKey: queryKeys.groupWithDashboard(variables.groupId),
      });
      // Invalidate settlement summary
      queryClient.invalidateQueries({
        queryKey: queryKeys.settlements.summary(variables.groupId),
      });
    },
  });
};

// Update settlement mutation
export const useUpdateSettlementMutation = () => {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      settlementId,
      data,
    }: {
      settlementId: string;
      groupId: string;
      data: Partial<CreateSettlementDto>;
    }) => {
      const response = await api.put<SettlementResponseDto>(
        `/settlements/${settlementId}`,
        data
      );
      // Handle both SuccessResponseDto and direct response
      if (response && typeof response === 'object' && 'data' in response) {
        return (response as SuccessResponseDto<unknown>).data;
      }
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate ALL group settlements queries (including filtered ones)
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'group', variables.groupId],
      });
      // Invalidate user settlements
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.settlements.all(), 'user'],
      });
      // Invalidate group balances
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupBalances(variables.groupId),
      });
      // Refetch simplified debts for both filtered and unfiltered queries (force immediate refetch)
      queryClient.refetchQueries({
        queryKey: queryKeys.simplifiedDebts(variables.groupId, true),
      });
      queryClient.refetchQueries({
        queryKey: queryKeys.simplifiedDebts(variables.groupId, false),
      });
      // Refetch group with dashboard (force immediate refetch)
      queryClient.refetchQueries({
        queryKey: queryKeys.groupWithDashboard(variables.groupId),
      });
      // Invalidate settlement summary
      queryClient.invalidateQueries({
        queryKey: queryKeys.settlements.summary(variables.groupId),
      });
    },
  });
};
